import { Input } from '@sitewise/ui';
import { useQuery } from '@tanstack/react-query';
import { MapPin, Sparkles } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
} from 'react';

import {
  autocompletePlaces,
  fetchPlaceDetails,
  fetchPlacesConfigured,
  type PlaceAddressDetail,
  type PlacesPrediction,
} from '../api/places.js';

interface Props {
  /** Current value of the street address input. */
  value: string;
  onChange: (value: string) => void;
  /** Called when the user picks a prediction. We pass the full parsed
   *  address so the building form can fill out city / state / zip. */
  onPlaceSelected: (place: PlaceAddressDetail) => void;
  id?: string;
  required?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
  /** Optional label rendered above the input for parity with the surrounding form. */
  // (no label — the calling form owns the <Label htmlFor=…>)
}

/**
 * Street-address input with Google Places autocomplete via our API proxy.
 *
 * Behaviour:
 *   - Predictions appear after 2 chars and 200 ms of idle typing
 *   - Up/Down/Enter/Esc work in the dropdown
 *   - Picking a prediction fires `onPlaceSelected` so the parent can
 *     auto-fill city / state / postal
 *   - When the server has no Places key configured we silently degrade
 *     to a plain text input (no dropdown, no extra UI noise)
 */
export function AddressAutocomplete({
  value,
  onChange,
  onPlaceSelected,
  id,
  required,
  autoFocus,
  placeholder,
}: Props) {
  const inputId = useId();
  const listboxId = useId();
  const fieldId = id ?? inputId;

  const [debounced, setDebounced] = useState(value);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [sessionToken, setSessionToken] = useState(() => crypto.randomUUID());
  const [loadingDetail, setLoadingDetail] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  // Tracks the most recent user-driven edit so we don't show the dropdown
  // immediately after a programmatic fill (e.g. picking a prediction).
  const lastUserEditRef = useRef(value);

  // Server probe for whether Places is configured at all. Cached forever
  // (per page load) — it's a constant for the lifetime of the API process.
  const configuredQuery = useQuery({
    queryKey: ['places-configured'],
    queryFn: fetchPlacesConfigured,
    staleTime: Infinity,
    retry: false,
  });
  const enabled = configuredQuery.data === true;

  // 200 ms debounce on the input value. Keeps Google calls (and our
  // rate limit) cheap while still feeling instant.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), 200);
    return () => clearTimeout(t);
  }, [value]);

  const predictionsQuery = useQuery({
    queryKey: ['places-autocomplete', debounced, sessionToken],
    queryFn: () => autocompletePlaces(debounced, sessionToken),
    enabled:
      enabled &&
      open &&
      debounced.trim().length >= 2 &&
      lastUserEditRef.current === debounced,
    staleTime: 60_000,
    retry: false,
  });

  const predictions: PlacesPrediction[] = predictionsQuery.data?.predictions ?? [];

  // Close the dropdown when the user clicks outside.
  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const pick = useCallback(
    async (p: PlacesPrediction) => {
      setOpen(false);
      setActiveIndex(-1);
      setLoadingDetail(true);
      try {
        const place = await fetchPlaceDetails(p.placeId, sessionToken);
        // The form owns the input value — we surface the parsed line1
        // (e.g. "120 Broadway") rather than the raw prediction text.
        lastUserEditRef.current = place.addressLine1;
        onChange(place.addressLine1);
        onPlaceSelected(place);
      } catch {
        // Swallow — the user can edit the fields manually. We deliberately
        // don't toast here because address pickers are noisy enough; a
        // failed details call just leaves the input in its typed state.
      } finally {
        setLoadingDetail(false);
        // Spec from Google: rotate the session token after each picked
        // prediction (one session = many autocomplete + one details call).
        setSessionToken(crypto.randomUUID());
      }
    },
    [onChange, onPlaceSelected, sessionToken],
  );

  function onInputChange(e: ChangeEvent<HTMLInputElement>) {
    const next = e.target.value;
    lastUserEditRef.current = next;
    onChange(next);
    if (enabled) setOpen(true);
    setActiveIndex(-1);
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (!enabled || !open || predictions.length === 0) {
      // Allow `ArrowDown` on an empty value (or before predictions load)
      // to open the dropdown if there's *something* to show.
      if (e.key === 'ArrowDown' && enabled && value.trim().length >= 2) setOpen(true);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(predictions.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(-1, i - 1));
    } else if (e.key === 'Enter') {
      if (activeIndex >= 0 && predictions[activeIndex]) {
        e.preventDefault();
        void pick(predictions[activeIndex]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setActiveIndex(-1);
    }
  }

  const showDropdown = useMemo(
    () => enabled && open && value.trim().length >= 2,
    [enabled, open, value],
  );

  return (
    <div className="relative" ref={wrapperRef}>
      <Input
        id={fieldId}
        value={value}
        onChange={onInputChange}
        onFocus={() => {
          if (enabled && value.trim().length >= 2) setOpen(true);
        }}
        onKeyDown={onKeyDown}
        required={required}
        autoFocus={autoFocus}
        placeholder={placeholder ?? '120 Broadway'}
        autoComplete="off"
        role="combobox"
        aria-expanded={showDropdown}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-activedescendant={
          activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined
        }
      />

      {/* Subtle hint that Google is powering the suggestions, only when
          a key is configured. Visible just under the input on the right. */}
      {enabled ? (
        <div className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-fg-muted">
          <Sparkles className="h-3.5 w-3.5" aria-hidden />
        </div>
      ) : null}

      {showDropdown ? (
        <ul
          id={listboxId}
          role="listbox"
          className="absolute z-50 mt-1 w-full overflow-hidden rounded-md border border-border bg-surface-elevated shadow-lg"
        >
          {predictionsQuery.isFetching && predictions.length === 0 ? (
            <li className="px-3 py-2 text-sm text-fg-muted">Searching…</li>
          ) : predictions.length === 0 ? (
            <li className="px-3 py-2 text-sm text-fg-muted">
              No matches. You can keep typing or fill the fields manually.
            </li>
          ) : (
            predictions.map((p, idx) => {
              const active = idx === activeIndex;
              return (
                <li
                  key={p.placeId}
                  id={`${listboxId}-option-${idx}`}
                  role="option"
                  aria-selected={active}
                  onMouseEnter={() => setActiveIndex(idx)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    void pick(p);
                  }}
                  className={`flex cursor-pointer items-start gap-2 px-3 py-2 text-sm ${
                    active ? 'bg-surface-muted text-fg' : 'text-fg-secondary hover:bg-surface-muted'
                  }`}
                >
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-fg-muted" aria-hidden />
                  <span className="min-w-0">
                    <span className="block truncate font-medium text-fg">{p.mainText}</span>
                    {p.secondaryText ? (
                      <span className="block truncate text-xs text-fg-muted">
                        {p.secondaryText}
                      </span>
                    ) : null}
                  </span>
                </li>
              );
            })
          )}
          {loadingDetail ? (
            <li className="border-t border-border px-3 py-2 text-xs text-fg-muted">
              Loading address…
            </li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}
